import { FC } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import { Column } from 'further-ui/components/Layout';
import { dateToLabel } from 'further-ui/utils';
import {
  formatSnippet,
  formatAmlTypes,
  sanitizeAmlData,
} from 'helpers/kyc/text-formatting';
import SanitizedHitDataDisplay from './SanitizedHitDataDisplay';
import RecordRow from './RecordRow';

const AmlDetailItemContainer = styled(Box)({
  padding: '0px',
  maxWidth: '70vw',
  overflowX: 'auto',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
});

const AmlDetailItemTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.sm),
}));

const StyledLink = styled(Link)({
  color: blue[500],
  textDecoration: 'underline',
  cursor: 'pointer',
});

type Props = {
  entity: {
    name: string;
    source?: string;
    url: string;
    aml_types: Array<string>;
    data: Array<{
      name?: string;
      value: string;
    }>;
    snippet?: string;
    published?: string;
  };
  index: number;
  type: string;
};

const AmlDetailItem: FC<Props> = ({ entity, index, type }) => {
  const processedData = sanitizeAmlData(entity.data);

  return (
    <AmlDetailItemContainer key={`${type}-${index}`}>
      <Column spacing="md">
        <AmlDetailItemTitle variant="h6">
          {formatAmlTypes(entity.aml_types).join(', ')}
        </AmlDetailItemTitle>

        <RecordRow label="Title:">
          {entity.url ? (
            <StyledLink
              href={entity.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {entity.name}
            </StyledLink>
          ) : (
            entity.name
          )}
        </RecordRow>

        {type === 'adverse-media' && (
          <RecordRow label="Published:">
            {dateToLabel(
              entity.published,
              '(No date)',
              false,
              false,
              'do MMMM yyyy',
            )}
          </RecordRow>
        )}

        {entity.snippet ? (
          <RecordRow label="Excerpt:">
            {formatSnippet(entity.snippet)}
          </RecordRow>
        ) : null}

        <SanitizedHitDataDisplay
          data={processedData}
          type={type}
          index={index}
        />
      </Column>
    </AmlDetailItemContainer>
  );
};

export default AmlDetailItem;
